// src/context/AuthContext.js
/**
 * Global authentication + dashboard-access context.
 *
 * Exposes:
 *   • `user`           Firebase user object (or `null` when signed-out).
 *   • `loading`        Flag while the initial Firebase auth state resolves.
 *   • `claims`         Firebase custom claims (role/clinic, …) from the ID token.
 *   • `accountContext` Backend account context from `GET /v1/users/me`.
 *   • `authorised`     Whether the user may access the dashboard (DASH-AUTH-1).
 *   • `contextLoading` Flag while the backend context is being fetched.
 *   • `contextError`   Truthy when the membership check could not be completed.
 *   • `refreshContext` Re-runs the backend membership check.
 *
 * Implementation notes:
 *   1. `onIdTokenChanged` fires once on mount, on auth-state changes, and when
 *      Firebase refreshes the current ID token or custom claims in place.
 *   2. As soon as Firebase returns a user we read its ID-token claims (no extra
 *      network round-trip) so role-based UI works immediately.
 *   3. The authoritative dashboard-access decision comes from the backend User
 *      Service via the gateway; we fetch it whenever the signed-in user changes.
 *   4. The "returning" flag (Cloud Storage metadata) is preserved for existing
 *      consumers and enriched without blocking the UI.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  getAuth,
  getIdTokenResult,
  onIdTokenChanged,
} from "firebase/auth";
import { getStorage, listAll, ref } from "firebase/storage";
import { firebaseApp } from "../firebase/firebaseApp";
import { fetchUserContext } from "../api/userContext";
import { fetchOrganisationContext } from "../api/organisations";
import { isDashboardAuthorised } from "../utils/authentication/dashboardAccess";

// Provide sensible defaults to avoid undefined checks in consumers.
const AuthContext = createContext({
  user: null,
  loading: true,
  claims: null,
  accountContext: null,
  legalStatus: null,
  authorised: false,
  contextLoading: false,
  contextError: null,
  organisation: null,
  organisationLoading: false,
  isBootstrapAdmin: false,
  refreshContext: async () => {},
  applyLegalStatus: () => {},
});

/**
 * React provider that supplies auth + dashboard-access state to its subtree.
 */
export function AuthProvider({ children }) {
  // Firebase SDK factories are idempotent; they return singletons.
  const auth = getAuth(firebaseApp);

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claims, setClaims] = useState(null);

  const [accountContext, setAccountContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [contextError, setContextError] = useState(null);
  const [organisation, setOrganisation] = useState(null);
  const [organisationLoading, setOrganisationLoading] = useState(false);
  const [isBootstrapAdmin, setIsBootstrapAdmin] = useState(false);
  const [authVersion, setAuthVersion] = useState(0);

  // Guards against state updates from a stale fetch after the user changes.
  const contextRequestId = useRef(0);

  /* ------------------------------------------------------------------ */
  /* Backend membership check (authoritative for dashboard access).     */
  /* ------------------------------------------------------------------ */
  const loadContext = useCallback(async () => {
    const requestId = (contextRequestId.current += 1);
    setContextLoading(true);
    setContextError(null);
    try {
      const context = await fetchUserContext();
      if (requestId !== contextRequestId.current) return; // superseded
      setAccountContext(context);
      return context;
    } catch (err) {
      if (requestId !== contextRequestId.current) return; // superseded
      setAccountContext(null);
      setContextError(err);
      if (process.env.NODE_ENV === "development") {
        console.error("Failed to load user context:", err);
      }
      return null;
    } finally {
      if (requestId === contextRequestId.current) {
        setContextLoading(false);
      }
    }
  }, []);

  const applyLegalStatus = useCallback((legalStatus) => {
    if (!legalStatus) {
      return;
    }

    setAccountContext((currentContext) => {
      if (!currentContext || typeof currentContext !== "object") {
        return { legalStatus };
      }

      return {
        ...currentContext,
        legalStatus,
      };
    });
  }, []);

  /* ------------------------------------------------------------------ */
  /* Subscribe to Firebase ID-token/auth changes once on mount.         */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        contextRequestId.current += 1; // cancel any in-flight context fetch
        setUser(null);
        setClaims(null);
        setAccountContext(null);
        setContextError(null);
        setContextLoading(false);
        setAuthVersion((currentValue) => currentValue + 1);
        setLoading(false);
        return;
      }

      // Expose bare user immediately for snappy UI updates.
      setUser(firebaseUser);
      setAuthVersion((currentValue) => currentValue + 1);
      setLoading(false);

      // Read custom claims from the ID token (no extra network call).
      try {
        const tokenResult = await getIdTokenResult(firebaseUser);
        setClaims(tokenResult?.claims ?? null);
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.error("Error reading ID token claims:", err);
        }
        setClaims(null);
      }

      // Kick off the authoritative backend membership check.
      loadContext();

      // Fetch a "returning" flag asynchronously (non-blocking, best-effort).
      try {
        const storage = getStorage(firebaseApp);
        const metadataRef = ref(storage, `user_metadata/${firebaseUser.uid}`);
        const res = await listAll(metadataRef);
        const returning = res.items.length > 0;
        setUser((prev) => (prev ? { ...prev, returning } : prev));
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.error("Error checking returning metadata:", err);
        }
        setUser((prev) => (prev ? { ...prev, returning: false } : prev));
      }
    });

    // Cleanup listener on unmount.
    return unsubscribe;
  }, [auth, loadContext]);

  /* ------------------------------------------------------------------ */
  /* Memoise context to keep reference stable between renders.          */
  /* ------------------------------------------------------------------ */
  const authorised = useMemo(
    () =>
      isDashboardAuthorised(accountContext, {
        claims,
        email: user?.email,
      }),
    [accountContext, claims, user?.email],
  );

  /* ------------------------------------------------------------------ */
  /* Load the authorised user's organisation context (DASH-AUTH-3).     */
  /* Cosmetic enrichment (org name/status) — does not gate access.      */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!user || !authorised) {
      setOrganisation(null);
      setOrganisationLoading(false);
      setIsBootstrapAdmin(false);
      return undefined;
    }

    let cancelled = false;
    setOrganisationLoading(true);
    fetchOrganisationContext()
      .then((context) => {
        if (cancelled) return;
        setOrganisation(context?.organisation ?? null);
        setIsBootstrapAdmin(Boolean(context?.isBootstrapAdmin));
      })
      .catch((err) => {
        if (cancelled) return;
        setOrganisation(null);
        setIsBootstrapAdmin(false);
        if (process.env.NODE_ENV === "development") {
          console.error("Failed to load organisation context:", err);
        }
      })
      .finally(() => {
        if (cancelled) return;
        setOrganisationLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [authVersion, authorised, user]);

  const contextValue = useMemo(
    () => ({
      user,
      loading,
      claims,
      accountContext,
      legalStatus: accountContext?.legalStatus ?? null,
      authorised,
      contextLoading,
      contextError,
      organisation,
      organisationLoading,
      isBootstrapAdmin,
      refreshContext: loadContext,
      applyLegalStatus,
    }),
    [
      user,
      loading,
      claims,
      accountContext,
      authorised,
      contextLoading,
      contextError,
      organisation,
      organisationLoading,
      isBootstrapAdmin,
      loadContext,
      applyLegalStatus,
    ],
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {/* Delay rendering children until first auth resolution completes. */}
      {!loading && children}
    </AuthContext.Provider>
  );
}

/**
 * Convenience hook for consuming the authentication context.
 */
export const useAuth = () => useContext(AuthContext);
